import { HttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import type {
  AccountingSyncStatus,
  BuildPendingAccountingRequest,
  BuildPendingAccountingResponse,
  SyncTransactionsMeta,
  SyncTransactionsResponse,
  WalletAccountingSyncTransactionDetail,
} from '../models';

export interface SyncTransactionsListFilters {
  page?: number;
  per_page?: number;
  status?: AccountingSyncStatus;
}

const LIST_PATH = '/wallet/accounting/sync-transactions';
const BUILD_PENDING_PATH = '/wallet/accounting/build-pending';

export class WalletAccountingSyncApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getList(filters: SyncTransactionsListFilters = {}): Promise<SyncTransactionsResponse> {
    const { page = 1, per_page = 25, status } = filters;
    const params: Record<string, string | number> = {
      page,
      per_page,
    };
    if (status) {
      params.status = status;
    }
    const raw = await this.httpClient.get<{
      data: SyncTransactionsResponse['data'];
      meta: Record<string, number> & Partial<SyncTransactionsMeta>;
    }>(LIST_PATH, params, {}, true);

    const m = raw.meta ?? {};
    const meta: SyncTransactionsMeta = {
      total: Number(m.total ?? 0),
      per_page: Number(m.per_page ?? (m as { perPage?: number }).perPage ?? per_page),
      current_page: Number(m.current_page ?? (m as { currentPage?: number }).currentPage ?? page),
      last_page: Number(m.last_page ?? (m as { lastPage?: number }).lastPage ?? 1),
    };

    return { data: Array.isArray(raw.data) ? raw.data : [], meta };
  }

  async getDetail(id: number): Promise<WalletAccountingSyncTransactionDetail> {
    return await this.httpClient.get<WalletAccountingSyncTransactionDetail>(`${LIST_PATH}/${id}`, {}, {}, true);
  }

  async downloadCsv(filters: { status?: AccountingSyncStatus } = {}): Promise<{ blob: Blob; filename: string }> {
    const params: Record<string, string | number> = { export: 1 };
    if (filters.status) {
      params.status = filters.status;
    }

    const response = await apiClientWH.get<Blob>(LIST_PATH, {
      params,
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'] || '';
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'wallet-accounting-sync.csv';

    return { blob: response.data, filename };
  }

  async buildPending(body: BuildPendingAccountingRequest = {}): Promise<BuildPendingAccountingResponse> {
    return await this.httpClient.post<BuildPendingAccountingResponse>(BUILD_PENDING_PATH, body);
  }
}
